import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FirebaseDatePipe } from '../../../pipes/firebase-date.pipe';

@Component({
    selector: 'app-progress-bar',
    standalone: true,
    imports: [CommonModule, MatIconModule, FirebaseDatePipe],
    templateUrl: './card-progress.component.html',
    styleUrls: ['./card-progress.component.scss']
})
export class ProgressBarComponent implements OnInit {
  @Input() dueDate!: Date | null;
  @Input() createdDate!: Date | null;
  @Input() lastDueDateChange!: Date | null;
  @Input() score!: number | null;
  @Input() priority!: number | null;
  @Input() lane!: string | null;
  @Input() inColumn: boolean = false;
  priorityText!: string;

  daysLeft!: number;
  progress!: number;
  displayDaysLeft!: string;
  
  ngOnInit(): void {
    console.log('Progress Bar Init:', {
      dueDate: this.dueDate,
      createdDate: this.createdDate,
      lastDueDateChange: this.lastDueDateChange
    });
    
    this.calculateProgress();
    this.priorityText = this.priorityLookup(this.priority ? this.priority : 3);
  }
  
  priorityLookup(index: number): string {
    switch (index) {
      case 1:
        return 'High';
      case 2:
        return 'Medium';
      case 3:
        return 'Low';
      default:
        return 'Low';
    }
  }

  calculateProgress(): void {
    // Ensure we have valid dates to work with
    if (!this.dueDate || !this.createdDate) {
      console.log('Missing dates for progress calculation', {
        dueDate: this.dueDate,
        createdDate: this.createdDate
      });
      this.displayDaysLeft = '';
      this.progress = 0;
      return;
    }
    
    try {
      const currentDate = new Date();
      
      // Convert timestamps if they're not already Date objects
      let createdDate: Date;
      if (this.lastDueDateChange) {
        createdDate = new Date(this.lastDueDateChange);
      } else {
        createdDate = new Date(this.createdDate);
      }
      
      const due = new Date(this.dueDate);
      
      console.log('Progress calculation with dates:', {
        currentDate,
        createdDate,
        dueDate: due
      });

      // Handle case where both created date and due date are in the future
      if (createdDate > currentDate) {
        console.log('Both dates are in the future, showing 0% progress');
        this.daysLeft = Math.ceil((due.getTime() - currentDate.getTime()) / (1000 * 3600 * 24));
        this.displayDaysLeft = `${this.daysLeft} days left`;
        this.progress = 0;
        return;
      }

      // Calculate total duration from created date to due date
      const totalDuration = Math.ceil((due.getTime() - createdDate.getTime()) / (1000 * 3600 * 24));

      // Calculate days left from current date to due date
      this.daysLeft = Math.ceil((due.getTime() - currentDate.getTime()) / (1000 * 3600 * 24));
      
      console.log('Progress calculation results:', {
        totalDuration,
        daysLeft: this.daysLeft
      });

      if (this.daysLeft > 0) {
        this.displayDaysLeft = `${this.daysLeft} days left`;
      } else if (this.daysLeft === 0) {
        this.displayDaysLeft = 'Due today';
      } else {
        this.displayDaysLeft = 'Past due';
      }

      // Calculate the progress percentage - improved formula
      if (totalDuration > 0) {
        // Original calculation based on elapsed time
        const elapsedProgress = Math.max(0, Math.min(100, ((totalDuration - this.daysLeft) / totalDuration) * 100));
        
        // If due date is close (within 7 days), increase the minimum progress to make it more visible
        if (this.daysLeft <= 7 && this.daysLeft >= 0) {
          // For tasks due today, show at least 80% progress
          // For tasks due in 7 days, show at least 20% progress
          // Scale linearly in between
          const minProgress = 80 - (this.daysLeft * 60 / 7);
          this.progress = Math.max(elapsedProgress, minProgress);
        } else if (this.daysLeft < 0) {
          // Past due tasks always show 100%
          this.progress = 100;
        } else {
          // For tasks due in more than 7 days, use the original calculation
          // but ensure at least 10% progress is shown for visibility
          this.progress = Math.max(elapsedProgress, 10);
        }
      } else {
        // If the created date and due date are the same or created date is after due date
        // Set progress based on whether the due date is in the past or future
        this.progress = this.daysLeft < 0 ? 100 : 0;
      }
      
      console.log('Final progress value:', this.progress);
    } catch (error) {
      console.error('Error calculating progress:', error);
      this.displayDaysLeft = '';
      this.progress = 0;
    }
  }
}